/**
 * AuditTimeline Component
 *
 * A reusable timeline component for displaying audit/activity logs.
 * Supports infinite scroll, action-type badges, and field-level change diffs.
 *
 * Usage:
 *   <AuditTimeline
 *     title="Contract Audit Logs"
 *     fetchLogs={fetchFn}
 *     open={open}
 *     onClose={() => setOpen(false)}
 *   />
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Calendar,
  Clock,
  Plus,
  Pencil,
  Trash2,
  ArrowRight,
  History,
  Loader2,
  User,
} from 'lucide-react';

import { cn } from '@/utils/cn';

// ============================================
// Types
// ============================================

/** A single field change within an audit entry */
export interface AuditFieldChange {
  field: string;
  oldValue: string | number | boolean | null;
  newValue: string | number | boolean | null;
}

/** A single audit log entry */
export interface AuditLogEntry {
  id: string;
  /** User who performed the action */
  userName: string;
  /** Optional user avatar URL */
  userAvatar?: string;
  /** Action type */
  action: 'CREATED' | 'UPDATED' | 'DELETED' | string;
  /** Human-readable description */
  description: string;
  /** Timestamp (ISO or any parseable date string) */
  timestamp: string;
  /** Optional list of changed fields */
  changes?: AuditFieldChange[];
}

/** Response shape for the fetch function */
export interface AuditLogResponse {
  list: AuditLogEntry[];
  totalRecords: number;
}

/** Fetch function signature the consumer must provide */
export type AuditFetchFn = (page: number, pageSize: number) => Promise<AuditLogResponse>;

export interface AuditTimelineProps {
  /** Whether the panel is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Panel title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Function to fetch audit logs (page is 0-indexed) */
  fetchLogs: AuditFetchFn;
  /** Records per page (default 10) */
  pageSize?: number;
}

// ============================================
// Helpers
// ============================================

const ACTION_CONFIG: Record<string, { icon: React.ElementType; label: string; color: string; bg: string }> = {
  CREATED: { icon: Plus, label: 'Created', color: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/30' },
  UPDATED: { icon: Pencil, label: 'Updated', color: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/30' },
  DELETED: { icon: Trash2, label: 'Deleted', color: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/30' },
};

function getActionConfig(action: string) {
  return ACTION_CONFIG[action.toUpperCase()] ?? {
    icon: Pencil,
    label: action,
    color: 'text-foreground-muted dark:text-foreground-dark-muted',
    bg: 'bg-secondary-100 dark:bg-secondary-800',
  };
}

function formatDate(raw: string) {
  try {
    const d = new Date(raw);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return raw;
  }
}

function formatTime(raw: string) {
  try {
    const d = new Date(raw);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function displayValue(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined || v === '') return 'Empty';
  return String(v);
}

// ============================================
// Sub-components
// ============================================

function ActionBadge({ action }: { action: string }) {
  const cfg = getActionConfig(action);
  const Icon = cfg.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold', cfg.bg, cfg.color)}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

function FieldChanges({ changes }: { changes: AuditFieldChange[] }) {
  return (
    <div className="mt-3 space-y-2">
      {changes.map((c, i) => (
        <div
          key={i}
          className="rounded-lg bg-emerald-50/60 dark:bg-emerald-900/20 border border-emerald-200/50 dark:border-emerald-800/30 px-3 py-2"
        >
          <span className="text-xs font-semibold text-foreground dark:text-foreground-dark">
            <span className="text-primary mr-0.5">*</span> {c.field}:
          </span>
          <div className="mt-1 flex items-center gap-2 text-xs">
            <span className="rounded bg-white dark:bg-secondary-800 border border-border dark:border-border-dark px-2 py-0.5 font-medium text-foreground-muted dark:text-foreground-dark-muted">
              {displayValue(c.oldValue)}
            </span>
            <ArrowRight className="h-3 w-3 text-foreground-muted dark:text-foreground-dark-muted shrink-0" />
            <span className="rounded bg-white dark:bg-secondary-800 border border-border dark:border-border-dark px-2 py-0.5 font-medium text-foreground dark:text-foreground-dark">
              {displayValue(c.newValue)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelineEntry({ entry }: { entry: AuditLogEntry }) {
  const cfg = getActionConfig(entry.action);

  return (
    <div className="relative flex gap-4 pb-8 last:pb-0">
      {/* Timeline line */}
      <div className="absolute left-[19px] top-10 bottom-0 w-px bg-border dark:bg-border-dark last:hidden" />

      {/* Timeline dot */}
      <div className={cn(
        'relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
        entry.action.toUpperCase() === 'DELETED' ? 'bg-red-500' : 'bg-primary',
        'text-white shadow-sm'
      )}>
        <cfg.icon className="h-4 w-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Date & time */}
        <div className="mb-2 flex items-center gap-3 text-xs text-foreground-muted dark:text-foreground-dark-muted">
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formatDate(entry.timestamp)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatTime(entry.timestamp)}
          </span>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-border dark:border-border-dark bg-card dark:bg-card-dark p-4 shadow-sm">
          {/* User & action */}
          <div className="flex items-center gap-3 mb-2">
            {entry.userAvatar ? (
              <img
                src={entry.userAvatar}
                alt={entry.userName}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                <User className="h-4 w-4" />
              </div>
            )}
            <div>
              <p className="text-sm font-bold text-foreground dark:text-foreground-dark leading-tight">
                {entry.userName}
              </p>
              <ActionBadge action={entry.action} />
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
            {entry.description}
          </p>

          {/* Field changes */}
          {entry.changes && entry.changes.length > 0 && (
            <FieldChanges changes={entry.changes} />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function AuditTimeline({
  open,
  onClose,
  title,
  subtitle = 'Track all changes and updates',
  fetchLogs,
  pageSize = 10,
}: AuditTimelineProps) {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const scrollRef = useRef<HTMLDivElement>(null);
  const hasMore = entries.length < total;

  // Reset when opened or fetchLogs changes
  useEffect(() => {
    if (!open) return;
    setEntries([]);
    setTotal(0);
    setPage(0);
    setInitialLoading(true);
  }, [open, fetchLogs]);

  // Fetch data
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const res = await fetchLogs(page, pageSize);
        if (cancelled) return;
        setEntries((prev) => (page === 0 ? res.list : [...prev, ...res.list]));
        setTotal(res.totalRecords);
      } catch (err) {
        console.error('Failed to fetch audit logs:', err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setInitialLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [open, page, pageSize, fetchLogs]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || isLoading || !hasMore) return;

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (nearBottom) {
      setPage((p) => p + 1);
    }
  }, [isLoading, hasMore]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 pb-8">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 flex w-full max-w-2xl flex-col rounded-2xl bg-background dark:bg-background-dark shadow-2xl max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center gap-3 rounded-t-2xl bg-primary px-6 py-4">
          <History className="h-6 w-6 text-white" />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white">{title}</h2>
            <p className="text-sm text-white/70">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-6"
        >
          {initialLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="mt-3 text-sm text-foreground-muted dark:text-foreground-dark-muted">
                Loading audit logs...
              </p>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <History className="h-12 w-12 text-foreground-muted dark:text-foreground-dark-muted" />
              <p className="mt-3 text-foreground-muted dark:text-foreground-dark-muted">
                No audit logs found
              </p>
            </div>
          ) : (
            <div>
              {entries.map((entry) => (
                <TimelineEntry key={entry.id} entry={entry} />
              ))}

              {/* Loading more indicator */}
              {isLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <span className="ml-2 text-sm text-foreground-muted dark:text-foreground-dark-muted">
                    Loading more...
                  </span>
                </div>
              )}

              {/* End of list */}
              {!hasMore && entries.length > 0 && (
                <p className="py-4 text-center text-xs text-foreground-muted dark:text-foreground-dark-muted">
                  All {total} records loaded
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
